/**
 * Anonymous trial session service.
 *
 * Lets visitors generate a small number of blogs without signing in. The
 * container (not the Functions app directly) issues the session — the
 * authoritative state lives in Cosmos DB, the client only holds an opaque
 * signed JWT in localStorage so the same person coming back gets the same
 * counter.
 *
 * Anti-abuse is enforced server-side:
 *   - Token is HS256-signed; the client cannot forge or increment.
 *   - Counter lives in Cosmos; clearing localStorage just orphans the token.
 *   - The Functions app rate-limits session creation per IP per 24h, so
 *     repeatedly clearing localStorage caps out quickly.
 */

const STORAGE_KEY = 'aitrend.anonymousSession.v1';
const API_BASE_URL = process.env.REACT_APP_CON_API_BASE_URL || 'http://localhost:3001';

// In-flight promise for session creation, so React StrictMode / double
// useEffects don't burn through two IP-rate-limit slots on first load.
let creationPromise = null;

function readStoredSession() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.token || !parsed?.sessionId) return null;
        return parsed;
    } catch (err) {
        console.warn('anonymousSessionService: failed to parse stored session', err);
        return null;
    }
}

function writeStoredSession(session) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (err) {
        console.warn('anonymousSessionService: failed to persist session', err);
    }
}

function clearStoredSession() {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

async function requestNewSession() {
    const response = await fetch(`${API_BASE_URL}/api/anonymous/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
    });
    if (!response.ok) {
        let body = {};
        try { body = await response.json(); } catch { /* ignore */ }
        const err = new Error(body.error || `Failed to create anonymous session (${response.status})`);
        err.status = response.status;
        err.code = body.code || 'ANON_SESSION_FAILED';
        throw err;
    }
    return response.json();
}

async function fetchSessionStatus(token) {
    const response = await fetch(`${API_BASE_URL}/api/anonymous/session/status`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'X-Anonymous-Session': token,
        },
    });
    if (!response.ok) {
        let body = {};
        try { body = await response.json(); } catch { /* ignore */ }
        const err = new Error(body.error || `Failed to load session (${response.status})`);
        err.status = response.status;
        err.code = body.code || 'ANON_SESSION_STATUS_FAILED';
        throw err;
    }
    return response.json();
}

function rememberSession({ sessionId, token, blogsGenerated, freeLimit, signupBonusBlogs }) {
    const session = {
        sessionId,
        token,
        blogsGenerated: Number(blogsGenerated) || 0,
        freeLimit: Number(freeLimit) || 2,
        signupBonusBlogs: Number(signupBonusBlogs) || 4,
        storedAt: new Date().toISOString(),
    };
    writeStoredSession(session);
    return session;
}

/**
 * Return the current anonymous session, creating one on first call.
 *
 * Subsequent calls reuse the cached token from localStorage. If the stored
 * token has been invalidated server-side (wiped session / expired JWT) we
 * transparently mint a fresh one — the IP rate limit still applies.
 */
export async function getOrCreateSession() {
    const stored = readStoredSession();
    if (stored) return stored;

    if (!creationPromise) {
        creationPromise = requestNewSession()
            .then((data) => rememberSession(data))
            .finally(() => {
                creationPromise = null;
            });
    }
    return creationPromise;
}

/**
 * Refresh the cached blog counter from the server. Call on app start so UI
 * gating reflects the true state even after devtools-based tampering.
 */
export async function refreshSession() {
    const stored = readStoredSession();
    if (!stored) return null;
    try {
        const status = await fetchSessionStatus(stored.token);
        const updated = {
            ...stored,
            blogsGenerated: Number(status.blogsGenerated) || 0,
            freeLimit: Number(status.freeLimit) || stored.freeLimit,
            signupBonusBlogs: Number(status.signupBonusBlogs) || stored.signupBonusBlogs,
        };
        writeStoredSession(updated);
        return updated;
    } catch (err) {
        if (err.status === 401 || err.status === 404) {
            // Server-side token was invalidated; drop it so the next call
            // mints a fresh one (subject to IP rate limit).
            clearStoredSession();
        }
        throw err;
    }
}

/**
 * Locally bump the blog counter after a successful generation. The server is
 * the authority, but updating the cached value means UI gates (like the
 * "show signup modal at 2") don't need an extra round-trip.
 */
export function recordLocalBlogGeneration() {
    const stored = readStoredSession();
    if (!stored) return null;
    const updated = {
        ...stored,
        blogsGenerated: (stored.blogsGenerated || 0) + 1,
    };
    writeStoredSession(updated);
    return updated;
}

export function peekStoredSession() {
    return readStoredSession();
}

export function getStoredToken() {
    return readStoredSession()?.token || null;
}

export function getStoredSessionId() {
    return readStoredSession()?.sessionId || null;
}

export function getBlogsUsed() {
    return readStoredSession()?.blogsGenerated || 0;
}

export function hasFreeTrialRemaining() {
    const stored = readStoredSession();
    if (!stored) return true;
    const limit = stored.freeLimit ?? 2;
    return (stored.blogsGenerated || 0) < limit;
}

export function clearSession() {
    clearStoredSession();
}

/**
 * Build the header dictionary for anonymous requests. Returns null when
 * there is no stored session (caller should fall back to authed headers).
 */
export function getAnonymousHeaders() {
    const stored = readStoredSession();
    if (!stored?.token) return null;
    return { 'X-Anonymous-Session': stored.token };
}
